import {ReactNode, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {requestContext} from '../../../common/requestContext';
import {ApiStatusCodeVo} from '../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {useFetchAgeAuth} from '../../../common/shared/hooks/useFetchAgeAuth';
import {Loading} from '../../../design-system/atoms/Loading/Loading';
import {getSerializedPagewebError} from '../../../errors/getSerializedPagewebError.function';
import {
  AgeAuthQueryVariables,
  ResultStatus,
} from '../../../graphql/generated/types';
import {setAge, setIsParentSelfCertificated} from '../../../user/UserSlice';

interface Props {
  fetchAgeAuthVariables?: AgeAuthQueryVariables;
  children?: ReactNode;
}

/**
 * 연령인증이 필요한 페이지(작품홈, 뷰어 등)에서 14세 미만 인증 유저인 경우 사용되는 Checker입니다.
 * 14세 미만 인증 유저의 경우 보호자 인증을 받아야합니다.
 * 하지만 본인 인증만 완료하고 보호자 인증 하지 않은 케이스가 있을 수 있습니다.
 * 이 케이스를 가려내기 위해 age_auth API를 호출합니다.
 * -907 result_code 가 내려오는경우 본인 인증만 완료하고 보호자 인증하지 않은 케이스로 판단합니다.
 */
export function Under14AgeHasParentCertificatedCheckerHeadless({
  fetchAgeAuthVariables = {ageLimit: 1},
  children,
}: Props) {
  const dispatch = useDispatch();
  const {data, error, isLoading} = useFetchAgeAuth({
    fetchAgeAuthVariables,
  });

  const shouldRemoveAuthAge = useMemo(() => {
    if (error) {
      return (
        error.resultCode ===
        ApiStatusCodeVo.Under14AgeAuthCompletedWithoutLegalAgentAuth
      );
    }
  }, [error]);

  useEffect(() => {
    // 인증 확인시 store에 age가 있는 경우 인증된 유저로 판단합니다.
    // age를 제거하여 인증되지 않은 유저로 변경하여 ContentAgeAuthLimitChecker 에서 인증 플로우를 타도록 합니다.
    if (shouldRemoveAuthAge) {
      dispatch(setAge(undefined));
    }
  }, [dispatch, shouldRemoveAuthAge]);

  useEffect(() => {
    if (data?.ageAuth?.status === ResultStatus.Success) {
      if (requestContext.requestInfo.user?.age !== data.ageAuth.age) {
        dispatch(setAge(data.ageAuth.age || undefined));
      }
      if (data.ageAuth.age) {
        dispatch(setIsParentSelfCertificated(true));
      }
    }
  }, [data?.ageAuth, dispatch]);

  if (isLoading) {
    return (
      <div className="flex-center-full">
        <Loading />
      </div>
    );
  }

  if (shouldRemoveAuthAge) {
    return null;
  }
  if (error) {
    throw getSerializedPagewebError(error);
  }

  return <>{children && children}</>;
}
